import * as React from 'react';
import { query, collection, orderBy, getDocs, getDoc, setDoc, doc } from "firebase/firestore";
import { History } from "./History";
import { database } from '../firebase-config';
import { updateAffectedRecipies } from '../data/RecipeData';
import { Timestamp } from "firebase/firestore";

const COLLECTION = "ingredients";

export class Ingredient {
  description = ""
  cost = ""
  per = ""
  total_price = ""
  total_quantity = ""
  information = ""
  is_deleted = false
  last_updated = ""
  history = new Array()
}

export class IngredientMaps {
  ingredientMap = new Map()
  ingredientMapNoDeleted = new Map()
}

export class Ingredients {
  options = new Array()
  docMap = new Map()
  refMap = new Map()
}

export const getIngredientMapsFromDB = async () => {
  const ingredientMaps = new IngredientMaps();
  try {
    const docQuery = query(collection(database, "ingredients"), orderBy("description", "asc"));
    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((doc) => {
      ingredientMaps.ingredientMap.set(doc.id, documentToIngredient(doc));
      if (!doc.data().is_deleted) {
        ingredientMaps.ingredientMapNoDeleted.set(doc.id, doc.data());
      }
    });
  } catch (e) {
    console.log("Error getting ingredients:", e);
    return new Map();
  }
  return ingredientMaps;

}

export const getIngredientArrayFromDB = async () => {
  const ingredientArray = new Array();
  try {
    const docQuery = query(collection(database, "ingredients"), orderBy("description", "asc"));
    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((doc) => {
      ingredientArray.push(documentToIngredient(doc));
    });
    return ingredientArray;
  } catch (e) {
    console.log("Error getting ingredients:", e);
    return new Array();
  }
}

export const getIngredient = async (documentRef) => {
  try {
    const existingDocRef = doc(database, COLLECTION, documentRef);
    const existingDoc = await getDoc(existingDocRef);
    return existingDoc;
  } catch (e) {
    console.log("getIngredient(): Error getting Recipe ref:" + documentRef);
  }
}

export const getIngredients = async (get_deleted) => {
  let ingredients = new Ingredients();

  try {
    const docQuery = query(collection(database, COLLECTION), orderBy("description", "asc"));

    const querySnapshot = await getDocs(docQuery);
    querySnapshot.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      if ((get_deleted)
        || (!get_deleted && !doc.data().is_deleted)) {
        ingredients.docMap.set(doc.id, doc.data());
        ingredients.refMap.set(doc.id, React.createRef());
        let option = { id: doc.id, value: doc.data().description };
        ingredients.options.push(option);
      }
    });
  } catch (e) {
    console.log("Error getting Ingredients:", e);
  }
  return ingredients;

}

export function getDocData(ingredient) {
  let now = Timestamp.now();

  let docData = {
    description: ingredient.description,
    cost: ingredient.cost,
    per: ingredient.per,
    total_price: ingredient.total_price,
    total_quantity: ingredient.total_quantity,
    information: ingredient.information,
    is_deleted: ingredient.is_deleted,
    last_updated: now,
    history: ingredient.history
  };
  return docData;
}

export function documentToIngredient(document) {
  let ingredient = new Ingredient();
  ingredient.document_reference = document.id;
  ingredient.description = document.data().description;
  ingredient.cost = document.data().cost;
  ingredient.per = document.data().per;
  ingredient.total_price = document.data().total_price;
  ingredient.total_quantity = document.data().total_quantity;
  ingredient.information = document.data().information;
  ingredient.is_deleted = document.data().is_deleted;
  ingredient.last_updated = document.data().last_updated;
  ingredient.history = document.data().history;

  return ingredient;
}

export const saveIngredient = async (settings, docRef, ingredientData, cost_changed) => {
  try {
    await setDoc(doc(database, COLLECTION, docRef), ingredientData, { merge: true });
    /**
     * Update all the recipes with this ingredient if the cost changed.
     */
    if(cost_changed) {
      updateAffectedRecipies(settings, docRef);
    }
  } catch (e) {
    console.log("Error saving Ingredient:", e);
  }
}

/**
 * ------------ Start History ----------------
 */
export const getHistoryRecord = async (history_key) => {
  let existingHistoryRef = doc(database, "history", history_key);
  let existingHistory = await getDoc(existingHistoryRef);
  let historyRecord = new History();
  historyRecord.key = history_key;
  historyRecord.updateDate = existingHistory.data().last_updated;
  historyRecord.field = existingHistory.data().field;
  historyRecord.oldValue = existingHistory.data().oldValue;
  historyRecord.newValue = existingHistory.data().newValue;
  return historyRecord;
}

export const loadHistoryMap = async (historyList) => {
  let map = new Map();
  let reversedHistory = historyList.reverse();
  for (let ii = 0; ii < 5; ii++) {
    try {
      let historyRecord = await getHistoryRecord(reversedHistory[ii]);
      map.set(reversedHistory[ii], historyRecord);
    } catch (e) {
      console.log("Error getting history ref:" + reversedHistory[ii]);
    }
  }
  return map;
}

export function addHistory(newHistoryList, now, ingredientName, field, oldValue, newValue) {
  let docData = {
    last_updated: now,
    field: field,
    oldValue: oldValue,
    newValue: newValue,
  };

  let documentName = 'ingredient_' + ingredientName + '_' + field + "_" + now;

  try {
    setDoc(doc(database, "history", documentName), docData);
  } catch (e) {
    console.log("Error saving History:", e);
    return;
  }

  newHistoryList.push(documentName);
}

export const recordHistory = async (newHistoryList, now, newIngredient, docRef) => {
  let existingDoc = "";
  try {
    let oldDocRef = doc(database, COLLECTION, docRef);
    existingDoc = await getDoc(oldDocRef);
  } catch (e) {
    console.log("Error getting existing Ingredient for History:", e);
  }

  let cost_changed = false;

  if (newIngredient.description !== existingDoc.data().description) {
    addHistory(newHistoryList, now, docRef, "description", existingDoc.data().description, newIngredient.description);
  }
  if (newIngredient.cost !== existingDoc.data().cost) {
    addHistory(newHistoryList, now, docRef, "cost", existingDoc.data().cost, newIngredient.cost);
    cost_changed = true;
  }
  if (newIngredient.per !== existingDoc.data().per) {
    addHistory(newHistoryList, now, docRef, "per", existingDoc.data().per, newIngredient.per);
  }
  if (newIngredient.total_price !== existingDoc.data().total_price) {
    addHistory(newHistoryList, now, docRef, "Total Price", existingDoc.data().total_price, newIngredient.total_price);
  }
  if (newIngredient.total_quantity !== existingDoc.data().total_quantity) {
    addHistory(newHistoryList, now, docRef, "Total Quantity", existingDoc.data().total_quantity, newIngredient.total_quantity);
  }
  if (newIngredient.information !== existingDoc.data().information) {
    addHistory(newHistoryList, now, docRef, "Information", existingDoc.data().information, newIngredient.information);
  }
  return cost_changed;
}
/**
 * ------------ End History ----------------
 */
